import React from "react";
import { observer } from "mobx-react";
import { UserUpdateStore } from "../../store";
import { UserUpdateForm } from "./UserUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { Language } from "../../../../api/graphql";
import { IdCardType } from "../../../../api/graphql";
import { Gender } from "../../../../api/graphql";

export type UserUpdateProps = {
  userUpdateStore: UserUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const UserUpdate: React.FC<UserUpdateProps> = observer(
  ({ userUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={userUpdateStore.isSaving} />
        <OutdatedAfterSaveNotice
          open={userUpdateStore.savedButOutdated}
          onReload={() => userUpdateStore.reloadUser()}
          reloadDisabled={userUpdateStore.isProcessing}
        />
        <UserUpdateForm
          userUpdateStore={userUpdateStore}
          nameError={userUpdateStore.errors.name?.message}
          onNameChange={(value: string) => {
            userUpdateStore.setNameValue(value);
          }}
          name={userUpdateStore.entityValues.name}
          emailError={userUpdateStore.errors.email?.message}
          onEmailChange={(value: string) => {
            userUpdateStore.setEmailValue(value);
          }}
          email={userUpdateStore.entityValues.email}
          roleError={userUpdateStore.errors.role?.message}
          onRoleChange={(value) => {
            userUpdateStore.setRoleValue(value);
          }}
          roleSelectedValue={userUpdateStore.entityValues.showRole}
          onRoleSelected={(value) => {
            userUpdateStore.setSelectedRole(value);
          }}
          roleOptions={userUpdateStore.getRoleOptions()}
          languageError={userUpdateStore.errors.language?.message}
          languageOptions={userUpdateStore.getLanguageOptions()}
          onLanguageChange={(value: Language) => {
            userUpdateStore.setLanguageValue(value);
          }}
          language={userUpdateStore.entityValues.language}
          avatarData={userUpdateStore.userInstance?.avatar_data}
          avatarEntityId={userUpdateStore.userInstance?.id || ""}
          avatarPreviewUrl={userUpdateStore.avatarPreviewUrl}
          onAvatarChange={(blob: Blob) =>
            userUpdateStore.avatarUploadBlob(blob)
          }
          onAvatarDelete={() => userUpdateStore.avatarDelete()}
          activeError={userUpdateStore.errors.active?.message}
          onActiveChange={(value: boolean) => {
            userUpdateStore.setActiveValue(value);
          }}
          active={userUpdateStore.entityValues.active}
          cellphoneError={userUpdateStore.errors.cellphone?.message}
          onCellphoneChange={(value: string) => {
            userUpdateStore.setCellphoneValue(value);
          }}
          cellphone={userUpdateStore.entityValues.cellphone}
          twoFaseAuthError={userUpdateStore.errors.twoFaseAuth?.message}
          onTwoFaseAuthChange={(value: boolean) => {
            userUpdateStore.setTwoFaseAuthValue(value);
          }}
          twoFaseAuth={userUpdateStore.entityValues.twoFaseAuth}
          idCardTypeError={userUpdateStore.errors.idCardType?.message}
          idCardTypeOptions={userUpdateStore.getIdCardTypeOptions()}
          onIdCardTypeChange={(value: IdCardType | "") => {
            userUpdateStore.setIdCardTypeValue(value);
          }}
          idCardType={userUpdateStore.entityValues.idCardType}
          idCardError={userUpdateStore.errors.idCard?.message}
          onIdCardChange={(value: string) => {
            userUpdateStore.setIdCardValue(value);
          }}
          idCard={userUpdateStore.entityValues.idCard}
          genderError={userUpdateStore.errors.gender?.message}
          genderOptions={userUpdateStore.getGenderOptions()}
          onGenderChange={(value: Gender | "") => {
            userUpdateStore.setGenderValue(value);
          }}
          gender={userUpdateStore.entityValues.gender}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={userUpdateStore.saveDisabled}
          onSave={() => userUpdateStore.saveUser()}
          onCancel={() => userUpdateStore.cancelUser()}
        />
        <ErrorDialog
          open={userUpdateStore.errorDialogOpened}
          title={userUpdateStore.errorTitle}
          description={userUpdateStore.errorDescription}
          onClose={() => userUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={userUpdateStore.confirmDialogOpened}
          title={userUpdateStore.confirmDialogTitle}
          description={userUpdateStore.confirmDialogDescription}
          onConfirm={() => userUpdateStore.executeConfirmedAction()}
          onCancel={() => userUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
